import { isPlatformServer } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  filter,
  from,
  map,
  of,
  retry,
  switchMap,
  take,
  throwError,
  timer,
} from 'rxjs';
import { AppConfigService } from '../providers/config.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StateService } from './state.service';
import { TabFocusService } from './tab-focus.service';

interface SocketEvent {
  name: string;
  payload: { [key: string]: unknown };
}

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private state = inject(StateService);
  private config = inject(AppConfigService);
  private platformId = inject<string>(PLATFORM_ID);
  private loader = inject(ObservableDataLoaderService);
  private tabFocusService = inject(TabFocusService);
  private socket: WebSocket;
  private retryCount = 0;
  deadTab = false;
  private _onSocketEvent = new BehaviorSubject<SocketEvent>(undefined);
  public onSocketEvent = this._onSocketEvent.pipe(
    switchMap(event =>
      this.tabFocusService.tabActive$.pipe(
        filter(Boolean),
        map(() => event)
      )
    )
  );
  public onOpen = new Subject<void>();

  private onOpen$ = this.onOpen.subscribe(() => {
    this.retryCount = 0;
  });

  private onDeadTab = this.tabFocusService.tabDead$.pipe(filter(Boolean)).subscribe(() => {
    this.deadTab = true;
    if (this.socket) this.socket.close();
  });

  public readonly onClose: Subject<void> = new Subject();
  private onClose$ = this.onClose.subscribe(() => {
    delete this.socket;
    const count = this.retryCount * this.retryCount;
    this.retryCount += 1;
    if (this.deadTab) {
      console.debug('Socket closed, tab is dead');
      return;
    }
    console.debug('Socket closed, reconnecting in ' + count + ' s');

    return timer(Math.max(1000 * count, 100))
      .pipe(
        switchMap(() => {
          if (this.socket) return of(true);
          this.loader.remove('socket');
          return this.connect();
        })
      )
      .subscribe();
  });

  public connect(): Observable<boolean> {
    if (isPlatformServer(this.platformId)) return;
    return this.loader.get('socket', () => {
      if (this.socket) {
        this.socket.onclose = () => {}; // preventing onClose from firing and reconnecting
        this.socket.close();
        delete this.socket;
      }
      return (this.state.tokenData ? from(this.state.tokenData.getIdToken()) : of(null)).pipe(
        switchMap(token => {
          const url = `${this.config.socketEndpoint}${token ? '?jwt=' : ''}${token ?? ''}`;
          this.socket = new WebSocket(url);

          this.socket.onmessage = ev => {
            this._onSocketEvent.next(JSON.parse(ev.data));
          };
          this.socket.onopen = () => this.onOpen.next();
          this.socket.onclose = () => this.onClose.next();
          this.socket.onerror = error => console.error('Socket error', error);

          return this.onOpen.pipe(map(() => true));
        })
      );
    });
  }

  public sendMessage(method: string, params: { [key: string]: string }) {
    return this.connect().pipe(
      take(1),
      switchMap(() => {
        if (!this.socket) return throwError(() => 'No socket after connect');
        if (this.socket.readyState !== 1) return throwError(() => 'Socket not ready');
        return of(true);
      }),
      retry({
        count: 2,
        delay: 250,
      }),
      map(() => this.socket.send(JSON.stringify({ params, method, jsonrpc: '2.0' }))),
      catchError(err => {
        console.error('Socket error', err);
        return of(false);
      })
    );
  }
}
